import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import {motion, useReducedMotion} from "motion/react";
import {useEffect, useRef, useState} from "react";
import type {ServerTableLoadingProgressProps} from "../types";

const MIN_VISIBLE_MS = 500;
const FINISH_DURATION_MS = 220;
const SWEEP_DURATION_SECONDS = 0.9;
const END_POSITION = '142.86%';

const ServerTableLoadingProgress = ({
    active,
    resetKey
}: ServerTableLoadingProgressProps) => {
    const theme = useTheme();
    const reduceMotion = useReducedMotion();

    const [visible, setVisible] = useState(active);
    const [finishing, setFinishing] = useState(false);
    const [animationCycle, setAnimationCycle] = useState(0);

    const visibleRef = useRef(active);
    const shownAtRef = useRef<number | null>(active ? Date.now() : null);
    const timerRefs = useRef<number[]>([]);

    useEffect(() => {
        const clearScheduledUpdates = () => {
            timerRefs.current.forEach(timer => window.clearTimeout(timer));
            timerRefs.current = [];
        };

        clearScheduledUpdates();

        if (active) {
            shownAtRef.current = Date.now();
            visibleRef.current = true;
            setVisible(true);
            setFinishing(false);
            setAnimationCycle(cycle => cycle + 1);

            return clearScheduledUpdates;
        }

        if (!visibleRef.current) {
            return clearScheduledUpdates;
        }

        const visibleFor = Date.now() - (shownAtRef.current ?? Date.now());
        const finishDelay = Math.max(MIN_VISIBLE_MS - visibleFor, 0);

        timerRefs.current.push(window.setTimeout(() => {
            setFinishing(true);

            timerRefs.current.push(window.setTimeout(() => {
                shownAtRef.current = null;
                visibleRef.current = false;
                setVisible(false);
                setFinishing(false);
            }, FINISH_DURATION_MS));
        }, finishDelay));

        return clearScheduledUpdates;
    }, [active, resetKey]);

    return (
        <Box
            aria-hidden={!visible}
            // Overlays the table's top edge so the bar takes no layout space; above the sticky header (zIndex 2).
            sx={{position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 3, overflow: 'hidden', pointerEvents: 'none'}}
        >
            {visible && (
                <motion.div
                    key={animationCycle}
                    initial={{x: reduceMotion ? '0%' : '-100%', opacity: 1}}
                    animate={{
                        x: reduceMotion
                            ? '0%'
                            : finishing
                                ? END_POSITION
                                : ['-100%', END_POSITION],
                        opacity: finishing ? 0 : 1
                    }}
                    transition={reduceMotion
                        ? {duration: finishing ? 0.15 : 0, ease: 'easeOut'}
                        : finishing
                            ? {duration: FINISH_DURATION_MS / 1000, ease: 'easeOut'}
                            : {duration: SWEEP_DURATION_SECONDS, ease: 'easeInOut', repeat: Infinity}
                    }
                    style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        width: '70%',
                        borderRadius: 999,
                        backgroundColor: theme.palette.primary.main,
                        willChange: 'transform'
                    }}
                />
            )}
        </Box>
    );
};

export default ServerTableLoadingProgress;
